import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Chip } from '@startuptools/ui/Chip';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Option } from '@startuptools/ui/Option';
import { Select } from '@startuptools/ui/Select';
import { Typography } from '@startuptools/ui/Typography';
import { type Control, Controller, type UseFormSetValue, useWatch } from 'react-hook-form';
import { useCurrentUserSuspenseQuery } from '../../../../graphql/react-operations';
import {
  type GqlCompleteShareCapitalChange,
  type GqlShareTransactionGroup,
  GqlTransactionKind,
} from '../../../../graphql/base-types.graphql';
import type { StgFormData } from './TransactionGroupForm';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useEffect } from 'react';
import { isNil } from 'lodash-es';
import { BigNumber } from 'bignumber.js';
import { useTransactionGroupFormSplitQuery } from './TransactionGroupFormSplit.graphql';
import { SplitLabel } from '../components/SplitLabel';
import React from 'react';

export const TransactionGroupFormSplit = ({
  control,
  setValue,
  stgs,
  currentScc,
}: {
  control: Control<StgFormData>;
  setValue: UseFormSetValue<StgFormData>;
  stgs: Pick<
    GqlShareTransactionGroup,
    'id' | 'transactionDate' | 'sortOrder' | 'kind' | 'snrShareCapitalChangeId' | 'splitFactor'
  >[];
  currentScc: GqlCompleteShareCapitalChange | undefined | null;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const {
    data: { currentUser: user },
  } = useCurrentUserSuspenseQuery();

  const { data, loading, error } = useTransactionGroupFormSplitQuery({
    variables: { companyId, input: { untilStgId: stgs.at(-1)?.id } },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!isNil(data?.shareState.totalNumberOfShares)) {
      setValue('sharesBefore', data.shareState.totalNumberOfShares.toString());
    }
  }, [data?.shareState.totalNumberOfShares, setValue]);

  const relevantStgs = stgs.filter(
    stg =>
      [GqlTransactionKind.Split, GqlTransactionKind.Merge].includes(stg.kind) &&
      stg.snrShareCapitalChangeId &&
      currentScc?.id.startsWith(stg.snrShareCapitalChangeId),
  );

  // Split factor that is already applied must be deducted
  const appliedSplitFactor = relevantStgs.reduce(
    (sum, stg) => new BigNumber(stg.splitFactor ?? 1).times(sum),
    new BigNumber(1),
  );
  const currentSplitFactor = React.useMemo(
    () => new BigNumber(currentScc?.splitFactor ?? 1),
    [currentScc?.splitFactor],
  );

  const defaultSharesAfter = React.useMemo(() => {
    if (isNil(data?.shareState.totalNumberOfShares)) {
      return;
    }

    return currentSplitFactor.dividedBy(appliedSplitFactor).times(data.shareState.totalNumberOfShares).toString();
  }, [data?.shareState.totalNumberOfShares, currentSplitFactor, appliedSplitFactor]);

  const maxSharesAfter = React.useMemo(() => {
    if (isNil(currentScc)) {
      /* interim splits are unlimited */
      return;
    }
    return defaultSharesAfter;
  }, [currentScc, defaultSharesAfter]);

  useEffect(() => {
    if (!defaultSharesAfter) return;

    setValue('sharesAfter', defaultSharesAfter.toString());
  }, [defaultSharesAfter, maxSharesAfter, setValue]);

  const selectedSharesBefore = useWatch({ control, name: 'sharesBefore' });
  const selectedSharesAfter = useWatch({ control, name: 'sharesAfter' });

  return (
    <>
      <FormControl disabled={loading}>
        <FormLabel>
          <Trans id="Number of shares before" />
        </FormLabel>
        <Controller
          control={control}
          name="sharesBefore"
          render={({ field }) => <NumericFormatInput {...field} size="sm" readOnly lang={i18n.locale} />}
        />
        <FormHelperText>
          <Trans id="Filled automatically" />
        </FormHelperText>
      </FormControl>

      <Controller
        control={control}
        name="sharesAfter"
        render={({ field, formState }) => {
          return (
            <FormControl disabled={loading} error={!!formState.errors.sharesAfter}>
              <FormLabel>
                <Trans id="Number of shares after" />
              </FormLabel>
              <NumericFormatInput {...field} size="sm" lang={i18n.locale} />
            </FormControl>
          );
        }}
      />

      {user.admin && (
        <FormControl disabled={loading}>
          <FormLabel>
            <Trans id="Rounding" />{' '}
            <Chip variant="outlined" size="sm">
              admin
            </Chip>
          </FormLabel>
          <Select
            name="roundUpShareholderId"
            onChange={(_event, value) => setValue('roundUpShareholderId', value as string)}
            slotProps={{ listbox: { sx: { zIndex: 999999 } } }}
          >
            {data?.shareState.ownerStates.map(os => (
              <Option value={os.shareholder.id} key={os.shareholder.id}>
                {os.shareholder.identity?.name}
              </Option>
            ))}
          </Select>
        </FormControl>
      )}
      {error ? (
        <Alert sx={{ mb: 2 }} color="warning">
          <Trans id="Could not load share count. Contact support for guidance." />
        </Alert>
      ) : (
        <Typography>
          <SplitLabel
            loading={loading || selectedSharesBefore === '0' || selectedSharesAfter === '0' || !selectedSharesAfter}
            before={selectedSharesBefore}
            after={selectedSharesAfter}
          />
        </Typography>
      )}
    </>
  );
};
